import { cn } from "@/lib/utils";
import { fill, type MyContributionsCopy } from "./my-contributions-copy";
import type { MonthPoint } from "./contribution-history";

interface MonthlyFlowProps {
  copy: MyContributionsCopy;
  months: MonthPoint[];
  locale: string;
  format: (value: number) => string;
}

/**
 * Validated audio per month, one series. Rejected and pending counts are in
 * the hover text and the screen-reader label, not in extra bars.
 */
export function MonthlyFlow({ copy, months, locale, format }: MonthlyFlowProps) {
  const total = months.reduce((sum, m) => sum + m.accepted, 0);
  const max = Math.max(1, ...months.map((m) => m.accepted));
  const short = new Intl.DateTimeFormat(locale, { month: "short" });
  const long = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" });
  const toDate = (month: string) => {
    const [y, m] = month.split("-").map(Number);
    return new Date(y, m - 1, 1);
  };

  return (
    <section aria-labelledby="mc-flow" className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="mc-flow" className="text-sm font-medium text-muted-foreground">
          {copy.flowTitle}
        </h2>
        <span className="text-xs tabular-nums text-faint-foreground">{fill(copy.flowTotal, { count: format(total) })}</span>
      </div>
      <div className="rounded-lg border border-border/60 bg-surface p-5">
        <ol className="grid h-40 grid-cols-6 items-end gap-3">
          {months.map((point) => {
            const label = fill(copy.flowBar, {
              month: long.format(toDate(point.month)),
              accepted: format(point.accepted),
              rejected: format(point.rejected),
              pending: format(point.pending),
            });
            return (
              <li key={point.month} className="flex h-full flex-col items-center justify-end gap-1.5" title={label}>
                <span className="sr-only">{label}</span>
                <span className="text-[11px] tabular-nums text-muted-foreground" aria-hidden="true">
                  {point.accepted > 0 ? format(point.accepted) : ""}
                </span>
                <span
                  aria-hidden="true"
                  className={cn("w-full max-w-8 rounded-t-[4px]", point.accepted > 0 ? "bg-primary" : "bg-border/70")}
                  style={{ height: `${point.accepted > 0 ? Math.max(6, (point.accepted / max) * 100) : 4}%` }}
                />
                <span className="text-[11px] capitalize text-faint-foreground" aria-hidden="true">
                  {short.format(toDate(point.month)).replace(".", "")}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
